import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, MessageSquare, Calendar, Clock, CheckCircle2, XCircle, Filter } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ParentProgressPage({ onLogout }) {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [sharedActivities, setSharedActivities] = useState([]);
  const [activities, setActivities] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, incomplete, with-comments
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (user?.role !== 'therapist') {
      navigate('/');
      return;
    }
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch child info
      const childResponse = await axios.get(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(childResponse.data);
      
      // Fetch activity completions
      const completionsResponse = await axios.get(`${API}/children/${childId}/activity-completions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletions(completionsResponse.data);
      
      // Create a map of completion by activity_id + weekly_focus_id
      const completionMap = {};
      completionsResponse.data.forEach(c => {
        completionMap[`${c.activity_id}_${c.weekly_focus_id}`] = c;
      });
      
      // Fetch all activities to get names
      const activitiesResponse = await axios.get(`${API}/activities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create a map of activity_id to activity
      const activityMap = {};
      activitiesResponse.data.forEach(a => {
        activityMap[a.id] = a;
      });
      setActivities(activityMap);
      
      // Fetch weekly focus history to get shared activities
      const weeklyFocusResponse = await axios.get(`${API}/weekly-focus/${childId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Build list of all shared activities with their completion status
      const allSharedActivities = [];
      for (const focus of weeklyFocusResponse.data || []) {
        for (const activityId of focus.activity_ids || []) {
          const completion = completionMap[`${activityId}_${focus.id}`];
          allSharedActivities.push({
            id: completion?.id || `${activityId}_${focus.id}`,
            activity_id: activityId,
            weekly_focus_id: focus.id,
            week_start_date: focus.week_start_date,
            focus_statement: focus.focus_statement,
            completed: completion?.completed || false,
            completed_at: completion?.completed_at,
            parent_comment: completion?.parent_comment,
            activity: activityMap[activityId]
          });
        }
      }
      
      // Sort by week date, most recent first
      allSharedActivities.sort((a, b) => new Date(b.week_start_date) - new Date(a.week_start_date));
      setSharedActivities(allSharedActivities);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load parent progress');
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = sharedActivities.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'completed') return a.completed;
    if (filter === 'incomplete') return !a.completed;
    if (filter === 'with-comments') return a.parent_comment && a.parent_comment.trim() !== '';
    return true;
  });

  const stats = {
    total: sharedActivities.length,
    completed: sharedActivities.filter(a => a.completed).length,
    withComments: sharedActivities.filter(a => a.parent_comment && a.parent_comment.trim() !== '').length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not completed';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center">
        <div className="animate-pulse text-stone-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/children/${childId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Parent Progress</h1>
              <p className="text-sm text-stone-500">{child?.name}'s activity completions</p>
            </div>
          </div>
          <Button variant="ghost" onClick={onLogout} className="text-stone-500">
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-white rounded-2xl border border-stone-100 text-center">
            <div className="text-3xl font-bold text-primary-900">{stats.total}</div>
            <div className="text-sm text-stone-500">Total Activities</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl border border-green-200 text-center">
            <div className="text-3xl font-bold text-green-700">{stats.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl border border-blue-200 text-center">
            <div className="text-3xl font-bold text-blue-700">{stats.withComments}</div>
            <div className="text-sm text-blue-600">With Comments</div>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-6">
          <Filter className="w-4 h-4 text-stone-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 rounded-full">
              <SelectValue placeholder="Filter activities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              <SelectItem value="completed">Completed Only</SelectItem>
              <SelectItem value="incomplete">Not Completed</SelectItem>
              <SelectItem value="with-comments">With Comments</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-stone-500">
            Showing {filteredActivities.length} of {sharedActivities.length}
          </span>
        </div>

        {/* Activities List */}
        {filteredActivities.length === 0 ? (
          <Card className="p-8 bg-white rounded-3xl border border-stone-100 text-center">
            <Check className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-stone-700 mb-1">No activities found</h3>
            <p className="text-sm text-stone-500">
              {filter === 'all' 
                ? "No activities have been shared via Weekly Focus yet"
                : "No activities match this filter"}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredActivities.map((item, index) => {
              const activity = item.activity;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`p-5 rounded-2xl border ${
                    item.completed 
                      ? 'bg-gradient-to-r from-green-50 to-white border-green-200' 
                      : 'bg-white border-stone-100'
                  }`}>
                    <div className="flex items-start gap-4">
                      {/* Status Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        item.completed 
                          ? 'bg-green-100' 
                          : 'bg-stone-100'
                      }`}>
                        {item.completed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-stone-400" />
                        )}
                      </div>
                      
                      {/* Activity Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-primary-900 truncate">
                            {activity?.name || 'Unknown Activity'}
                          </h3>
                          {item.completed ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Completed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              Pending
                            </Badge>
                          )}
                        </div>
                        
                        {/* Week info */}
                        <div className="flex items-center gap-1 text-xs text-stone-400 mb-2">
                          <Calendar className="w-3 h-3" />
                          <span>Week of {new Date(item.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        
                        {activity?.description && (
                          <p className="text-sm text-stone-500 mb-2 line-clamp-2">
                            {activity.description}
                          </p>
                        )}
                        
                        {/* Completion Time */}
                        {item.completed_at && (
                          <div className="flex items-center gap-1 text-xs text-green-600 mb-2">
                            <Clock className="w-3 h-3" />
                            <span>Completed: {formatDate(item.completed_at)}</span>
                          </div>
                        )}
                        
                        {/* Parent Comment */}
                        {item.parent_comment && item.parent_comment.trim() && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                            <div className="flex items-center gap-2 mb-1">
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                              <span className="text-xs font-medium text-blue-700">Parent Comment</span>
                            </div>
                            <p className="text-sm text-blue-900">{item.parent_comment}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
